import { ref } from 'vue'

// API base URL - automatically switches between dev and prod
const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8787'  // Development URL
  : 'https://walkywalky-api.angelikalevluh.workers.dev' // Production URL - replace with your actual URL

// Auth state
const isAuthenticated = ref(false)
const userEmail = ref('')
const accessCode = ref('')
const userData = ref<any>(null)

// Initialize auth state from localStorage
const initializeAuth = () => {
  const savedUserData = localStorage.getItem('walkywalky_user_data')
  
  if (savedUserData) {
    try {
      const user = JSON.parse(savedUserData)

      if (!user.email || !user.code) {
        console.log('❌ User data is missing email or code');
        isAuthenticated.value = false;
        return
      }

      userData.value = user
      userEmail.value = user.email || ''
      accessCode.value = user.code || ''
      isAuthenticated.value = true
      console.log('✅ User authenticated from localStorage:', user)
    } catch (error) {
      console.error('❌ Error parsing saved user data:', error)
    }
  }
}

// Login function
export const login = async (email: string, code: string): Promise<boolean> => {
  try {
    console.log('🔐 Attempting login with:', { email, code })
    console.log('🌐 Using API URL:', API_BASE_URL)
    
    const response = await fetch(`${API_BASE_URL}/api/codes/activate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email.trim(),
        code: code.trim()
      })
    })
    
    // Check if response is offline/network error
    const responseText = await response.text()
    console.log('🔐 Raw response:', responseText)
    
    if (responseText === 'Offline' || responseText.includes('offline')) {
      throw new Error('NETWORK_ERROR')
    }
    
    let data
    try {
      
      data = JSON.parse(responseText)
    } catch (parseError) {
      console.error('❌ JSON parse error:', parseError)
      throw new Error('NETWORK_ERROR')
    }
    console.log('🔐 Login response:', data)
    
    if (data.success) {
      // Save complete user data to localStorage
      const completeUserData = {
        email: email.trim(),
        code: code.trim(),
        ...data.data // Include all data from API response
      }
      
      localStorage.setItem('walkywalky_user_data', JSON.stringify(completeUserData))
      
      // Update state
      userData.value = completeUserData
      userEmail.value = email.trim()
      accessCode.value = code.trim()
      isAuthenticated.value = true

      console.log('✅ Login successful, saved user data:', completeUserData)
      return true
    } else {
      console.error('❌ Login failed:', data.error)
      throw new Error(data.error || 'Login failed')
    }
  } catch (error: any) {
    console.error('❌ Login error!:', error)
    
    // Check if it's a network error
    if (error.name === 'TypeError' && error.message?.includes('fetch') || 
        error.message?.includes('Failed to fetch') || 
        error.message?.includes('network')) {
      throw new Error('NETWORK_ERROR')
    } else {
      throw new Error(error.message || 'Login failed. Please try again.')
    }
  }
}

// Logout function
export const logout = () => {

  // Clear localStorage
  localStorage.removeItem('walkywalky_user_data')
  localStorage.removeItem('walkywalky_email') // Legacy cleanup
  localStorage.removeItem('walkywalky_code') // Legacy cleanup
  
  // Clear state
  userData.value = null
  userEmail.value = ''
  accessCode.value = ''
  isAuthenticated.value = false
  
  console.log('🚪 User logged out')
}

// Check if user is authenticated
export const checkAuth = (): boolean => {
  return isAuthenticated.value
}

// Get current user data
export const getCurrentUser = () => {
  return {
    email: userEmail.value,
    isAuthenticated: isAuthenticated.value,
    userData: userData.value
  }
}

// Get complete user data
export const getUserData = () => {
  return userData.value
}

// Export reactive state for components
export const AuthService = {
  login,
  logout,
  checkAuth,
  getCurrentUser,
  getUserData,
  initializeAuth,
  isAuthenticated,
  userEmail,
  accessCode,
  userData
}
